import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CaretLeft, CaretRight, DownloadSimple, ShieldCheck } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPE_LABELS = {
  condizioni: "Condizioni Generali",
  vessatorie: "Clausole vessatorie",
  addendum_agenzie: "Addendum Agenzie",
};
const CONTEXT_LABELS = {
  trial_signup: "Signup trial",
  checkout: "Checkout",
  workspace_create: "Creazione workspace",
};

export default function AdminTermsAcceptancesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 50;
  const [email, setEmail] = useState("");
  const [docType, setDocType] = useState("");
  const [context, setContext] = useState("");
  const [docVersion, setDocVersion] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildParams = () => {
    const p = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (email) p.set("email", email);
    if (docType) p.set("doc_type", docType);
    if (context) p.set("context", context);
    if (docVersion) p.set("doc_version", docVersion);
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/terms-acceptances?${buildParams()}`, { withCredentials: true });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) toast.error("Accesso riservato agli amministratori");
      else toast.error("Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, docType, context]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      // Riusa i filtri ma rimuove paginazione
      const p = new URLSearchParams();
      if (email) p.set("email", email);
      if (docType) p.set("doc_type", docType);
      if (context) p.set("context", context);
      if (docVersion) p.set("doc_version", docVersion);
      if (from) p.set("from_date", from);
      if (to) p.set("to_date", to);
      const resp = await axios.get(`${API}/admin/terms-acceptances/export.csv?${p}`, {
        withCredentials: true, responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const ts = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 19);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `terms_acceptances_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Accesso riservato" : "Errore export");
    } finally { setExporting(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const applyFilters = () => { setPage(1); load(); };

  return (
    <div className="space-y-4" data-testid="admin-terms-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-[#0D1F3C] flex items-center gap-2">
            <ShieldCheck size={28} weight="duotone" className="text-[#1B4FD8]" />
            Console Admin · Accettazioni Termini
          </h1>
          <p className="text-sm text-[#0D1F3C]/60 mt-1" data-testid="admin-terms-total">
            {total} record totali
          </p>
        </div>
        <Button
          onClick={exportCsv}
          disabled={exporting || total === 0}
          className="bg-[#1B4FD8] hover:bg-[#0D1F3C] gap-2"
          data-testid="admin-terms-export-csv"
        >
          <DownloadSimple size={16} />
          {exporting ? "Esportazione…" : "Esporta CSV"}
        </Button>
      </div>

      <div className="bg-white border border-[#0D1F3C]/8 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
        <div className="lg:col-span-2">
          <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Ricerca utente</label>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
            placeholder="email, nome o azienda…"
            data-testid="admin-terms-search"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Documento</label>
          <Select value={docType || "all"} onValueChange={(v) => setDocType(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="admin-terms-filter-doc"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="condizioni">Condizioni</SelectItem>
              <SelectItem value="vessatorie">Vessatorie</SelectItem>
              <SelectItem value="addendum_agenzie">Addendum Agenzie</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Contesto</label>
          <Select value={context || "all"} onValueChange={(v) => setContext(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="admin-terms-filter-ctx"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="trial_signup">Signup trial</SelectItem>
              <SelectItem value="checkout">Checkout</SelectItem>
              <SelectItem value="workspace_create">Creazione workspace</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Versione</label>
          <Input
            value={docVersion}
            onChange={(e) => setDocVersion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
            placeholder="es: v1.1-2026-05-23"
            data-testid="admin-terms-filter-version"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Da</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="admin-terms-filter-from" />
        </div>
        <div>
          <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">A</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="admin-terms-filter-to" />
        </div>
        <div className="lg:col-span-6 flex justify-end">
          <Button onClick={applyFilters} variant="outline" data-testid="admin-terms-apply">Applica filtri</Button>
        </div>
      </div>

      <div className="bg-white border border-[#0D1F3C]/8 rounded-xl overflow-hidden">
        <div className="max-h-[calc(100vh-380px)] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0D1F3C] text-white text-left sticky top-0">
              <tr>
                <th className="px-4 py-3 whitespace-nowrap">Quando</th>
                <th className="px-4 py-3">Utente</th>
                <th className="px-4 py-3">Documento</th>
                <th className="px-4 py-3">Versione</th>
                <th className="px-4 py-3">Contesto</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">User-Agent</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-[#0D1F3C]/40">Caricamento…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-[#0D1F3C]/40">Nessun record</td></tr>
              ) : items.map((it, i) => (
                <tr key={it.id || i} className="border-t border-[#0D1F3C]/5 hover:bg-[#F4F7FB]" data-testid={`admin-terms-row-${i}`}>
                  <td className="px-4 py-3 text-xs text-[#0D1F3C]/70 whitespace-nowrap">
                    {it.timestamp ? new Date(it.timestamp).toLocaleString("it-IT") : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-medium text-[#0D1F3C]">{it.user_email || "—"}</div>
                    {it.user_name && <div className="text-[#0D1F3C]/50">{it.user_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <Badge variant="outline">{DOC_TYPE_LABELS[it.doc_type] || it.doc_type}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-[#0D1F3C]/70">{it.doc_version || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    <Badge variant="secondary" className="bg-[#1B4FD8]/10 text-[#1B4FD8] border-0">
                      {CONTEXT_LABELS[it.context] || it.context}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#0D1F3C]/60 font-mono">{it.ip_address || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#0D1F3C]/50 truncate max-w-[200px]" title={it.user_agent || ""}>
                    {(it.user_agent || "—").slice(0, 60)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 bg-[#F4F7FB] border-t border-[#0D1F3C]/5">
          <span className="text-xs text-[#0D1F3C]/60">{total} record</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} data-testid="admin-terms-prev"><CaretLeft size={14} /></Button>
            <span className="text-sm font-medium">{page} / {totalPages}</span>
            <Button size="sm" variant="outline" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} data-testid="admin-terms-next"><CaretRight size={14} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
